import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Clock, User, Building2, Trash2 } from "lucide-react";
import api, { formatDateTime } from "@/lib/api";
import { toast } from "sonner";
import { usePermission } from "@/hooks/usePermission";

export default function AdminPendingActions() {
  const { canEdit } = usePermission("solicitacoes");
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  const fetchActions = useCallback(async () => {
    try { const res = await api.get("/admin/pending-actions"); setActions(res.data); } catch { toast.error("Erro ao carregar solicitações"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchActions(); }, [fetchActions]);

  const handleApprove = async (id) => {
    setProcessing(id);
    try {
      await api.post(`/admin/pending-actions/${id}/approve`);
      const action = actions.find(a => a.id === id);
      toast.success(`Solicitação de ${action?.target_name || "usuário"} aprovada`);
      fetchActions();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
    setProcessing(null);
  };

  const handleReject = async (id) => {
    setProcessing(id);
    try {
      await api.post(`/admin/pending-actions/${id}/reject`);
      const action = actions.find(a => a.id === id);
      toast.success(`Solicitação de ${action?.target_name || "usuário"} rejeitada`);
      fetchActions();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
    setProcessing(null);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="loading-spinner w-8 h-8" /></div>;

  return (
    <div className="p-4 pt-16 lg:pt-4 lg:p-8 space-y-6" data-testid="admin-pending-actions">
      <div>
        <h1 className="text-xl lg:text-2xl font-bold tracking-tight flex items-center gap-2">
          <Clock className="w-5 h-5 text-orange-400" /> Solicitações Pendentes
        </h1>
        <p className="text-sm text-muted-foreground mt-1">{actions.length} solicitação{actions.length !== 1 ? "ões" : ""} aguardando</p>
      </div>

      {actions.length === 0 ? (
        <Card className="border-white/5" data-testid="pending-actions-empty">
          <CardContent className="p-8 text-center text-muted-foreground">
            Nenhuma solicitação pendente
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {actions.map((a) => (
            <Card key={a.id} className="border-white/5" data-testid={`action-${a.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded text-xs font-semibold bg-red-500/15 text-red-400">
                        <Trash2 className="w-3 h-3 inline mr-1" />
                        {a.type === "delete_master" ? "Exclusão de Master" : a.type}
                      </span>
                      <span className="px-2 py-0.5 rounded text-xs font-semibold bg-yellow-500/15 text-yellow-400">
                        Aguardando
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Building2 className="w-3.5 h-3.5" /> {a.company_name}
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <User className="w-3.5 h-3.5" /> {a.target_name} ({a.target_email})
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Solicitado por: {a.requested_by_name} · {formatDateTime(a.created_at)} · Expira: {formatDateTime(a.expires_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button data-testid={`approve-action-${a.id}`} onClick={() => handleApprove(a.id)}
                      disabled={processing === a.id || !canEdit} size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
                      <CheckCircle className="w-4 h-4 mr-1" /> Aprovar
                    </Button>
                    <Button data-testid={`reject-action-${a.id}`} onClick={() => handleReject(a.id)}
                      disabled={processing === a.id || !canEdit} size="sm" variant="outline"
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                      <XCircle className="w-4 h-4 mr-1" /> Rejeitar
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
